// Wall-clock <-> UTC conversion in an arbitrary IANA time zone, with Intl
// only (the app has no date library). A `datetime-local` input yields a wall
// time with no zone ("2026-10-21T14:00"); `new Date(wallTime)` would read it
// in the browser's zone, which is wrong whenever the store sits elsewhere.

const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

const pad = (n) => String(n).padStart(2, "0");

// Date.UTC reads years 0–99 as 1900–1999; setUTCFullYear takes the year as
// given, so "0026-…" stays year 26.
const utcMs = (year, monthIndex, day, hour, minute, second) => {
  const date = new Date(0);
  date.setUTCFullYear(year, monthIndex, day);
  date.setUTCHours(hour, minute, second, 0);
  return date.getTime();
};

const formatters = new Map();
const formatterFor = (timeZone) => {
  const key = timeZone ?? "";
  if (!formatters.has(key)) {
    formatters.set(
      key,
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    );
  }
  return formatters.get(key);
};

// The calendar fields of `date` as seen on a clock in `timeZone`.
const wallPartsAt = (date, timeZone) => {
  const parts = {};
  for (const { type, value } of formatterFor(timeZone).formatToParts(date)) {
    if (type !== "literal") parts[type] = Number(value);
  }
  // Some engines render midnight as hour 24 even with h23.
  parts.hour %= 24;
  return parts;
};

// Milliseconds the zone is ahead of UTC at instant `epochMs`.
const offsetAt = (epochMs, timeZone) => {
  const whole = Math.floor(epochMs / 1000) * 1000;
  const p = wallPartsAt(new Date(whole), timeZone);
  return utcMs(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - whole;
};

// Answered once per zone: a valid zone's formatter goes into the cache above
// (it is the one the conversions use anyway), an invalid one is remembered here.
const invalidZones = new Set();
export const isValidTimeZone = (timeZone) => {
  if (!timeZone || invalidZones.has(timeZone)) return false;
  try {
    formatterFor(timeZone);
    return true;
  } catch {
    invalidZones.add(timeZone);
    return false;
  }
};

const DAY_MS = 24 * 60 * 60 * 1000;

// "YYYY-MM-DDTHH:MM" read as a clock in `timeZone` -> ISO UTC string, or null
// for input that isn't a wall time — including one shaped like it but naming
// a date or time that doesn't exist ("2026-02-30", month 13, hour 25, minute
// 60), which Date would otherwise roll into the next day, month or year.
// The zone's offsets a day either side are
// the only candidates (no zone changes offset twice in two days); a candidate
// counts if the zone really has that offset at the instant it yields. Around
// DST this resolves like Temporal's "compatible": a wall time repeated at
// fall-back takes its earlier instant, one skipped by spring-forward moves
// forward by the gap.
export const zonedWallTimeToUtcIso = (wallTime, timeZone) => {
  const match = WALL_TIME.exec(wallTime ?? "");
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match.map((part) => Number(part ?? 0));
  const wallAsUtc = utcMs(y, mo - 1, d, h, mi, s);
  // Date normalises out-of-range fields instead of rejecting them, so a field
  // that reads back different was out of range.
  const check = new Date(wallAsUtc);
  if (
    check.getUTCFullYear() !== y ||
    check.getUTCMonth() !== mo - 1 ||
    check.getUTCDate() !== d ||
    check.getUTCHours() !== h ||
    check.getUTCMinutes() !== mi ||
    check.getUTCSeconds() !== s
  ) {
    return null;
  }

  const offsetBefore = offsetAt(wallAsUtc - DAY_MS, timeZone);
  const offsetAfter = offsetAt(wallAsUtc + DAY_MS, timeZone);
  const matching = [...new Set([offsetBefore, offsetAfter])]
    .map((offset) => wallAsUtc - offset)
    .filter((instant) => offsetAt(instant, timeZone) === wallAsUtc - instant);

  const instant = matching.length
    ? Math.min(...matching)
    : wallAsUtc - offsetBefore;
  return new Date(instant).toISOString();
};

// ISO instant -> "YYYY-MM-DDTHH:MM" on a clock in `timeZone`, the value a
// `datetime-local` input takes. "" for a missing or unparseable instant.
export const utcIsoToZonedWallTime = (iso, timeZone) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const p = wallPartsAt(date, timeZone);
  const year = String(p.year).padStart(4, "0");
  return `${year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
};

// Display string for an instant on a clock in `timeZone`. Without `options`
// it matches the plain `toLocaleString()` the table used before, just pinned
// to the zone.
export const formatInZone = (iso, timeZone, locale, options = {}) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(locale, { ...options, timeZone });
};
